import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, addDoc, deleteDoc, doc } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
import AddCardForm from './AddCardForm';

const PhotoAlbum = React.forwardRef(({ user, onClose, hideHeader, hideUpload }, ref) => {
  const [loading, setLoading] = useState(true);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showAddCard, setShowAddCard] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  useEffect(() => {
    const initializeAlbum = async () => {
      console.log('Initializing album with user:', user?.uid);
      if (user?.uid) {
        await loadPhotos();
      } else {
        console.error('No user or user.uid available');
      }
      setLoading(false);
    };

    initializeAlbum();
  }, [user]);

  const loadPhotos = async () => {
    console.log('Loading photos for user:', user.uid);
    const q = query(collection(db, 'photoAlbum'), where('userId', '==', user.uid));
    const snapshot = await getDocs(q);
    console.log('Photos loaded:', snapshot.docs.length);
    const loadedPhotos = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    console.log('Processed photos:', loadedPhotos);
    setPhotos(loadedPhotos);
  };

  const handlePhotoUpload = async (e) => {
    if (!user?.uid) {
      console.error('No user ID available');
      return;
    }

    const file = e.target.files[0];
    if (!file) {
      console.log('No file selected');
      return;
    }

    console.log('Starting photo upload...', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      userId: user?.uid
    });

    try {
      setUploading(true);
      const fileRef = storageRef(storage, `photoAlbum/${user.uid}/${Date.now()}-${file.name}`);
      console.log('Storage reference created:', fileRef.fullPath);

      const uploadResult = await uploadBytes(fileRef, file);
      console.log('File uploaded to storage:', uploadResult);

      const url = await getDownloadURL(fileRef);
      console.log('Download URL obtained:', url);

      const docRef = await addDoc(collection(db, 'photoAlbum'), {
        userId: user.uid,
        imageUrl: url,
        createdAt: new Date().toISOString(),
        storagePath: fileRef.fullPath
      });
      console.log('Document added to Firestore:', docRef.id);

      await loadPhotos();
      console.log('Photos reloaded');
    } catch (error) {
      console.error('Detailed upload error:', error);
      if (error.code) {
        console.error('Error code:', error.code);
      }
      if (error.message) {
        console.error('Error message:', error.message);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleCreateCard = (photo) => {
    setSelectedPhoto(photo);
    setShowAddCard(true);
  };

  const handleDeletePhoto = async (photo) => {
    try {
      // Delete from storage
      const fileRef = storageRef(storage, photo.storagePath);
      await deleteObject(fileRef);
      
      // Delete from Firestore
      await deleteDoc(doc(db, 'photoAlbum', photo.id));
      
      loadPhotos();
    } catch (error) {
      console.error('Error deleting photo:', error);
    }
  };

  // Expose handlePhotoUpload through ref instead of DOM
  React.useImperativeHandle(ref, () => ({
    handlePhotoUpload
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {!hideHeader && (
        <div className="mb-6 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Photo Gallery</h1>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {photos.map((photo) => (
          <div key={photo.id} className="relative group">
            <img
              src={photo.imageUrl}
              alt=""
              className="w-full h-48 object-cover rounded-lg"
            />
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-opacity flex items-center justify-center opacity-0 group-hover:opacity-100">
              <button
                onClick={() => handleCreateCard(photo)}
                className="bg-white text-gray-800 px-3 py-1 rounded mr-2"
              >
                Create Card
              </button>
              <button
                onClick={() => handleDeletePhoto(photo)}
                className="bg-red-500 text-white px-3 py-1 rounded"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Add Card Modal */}
      {showAddCard && (
        <AddCardForm
          onClose={() => {
            setShowAddCard(false);
            setSelectedPhoto(null);
          }}
          userId={user.uid}
          initialImage={{
            type: 'albumPhoto',
            photoId: selectedPhoto.id,
            url: selectedPhoto.imageUrl
          }}
        />
      )}
    </div>
  );
});

export default PhotoAlbum; 